<template>
  <div class="availability-wizard m-x-auto" style="max-width: 560px;">
    <!-- In the app embed the native nav bar already titles the screen -->
    <div v-if="!forAppEmbed" class="m-b-m">
      <h1 class="font-heavy m-y-none">Add Availability</h1>
      <p class="text-14 text-gray-500 m-t-xxs m-b-none">Open time slots wrestlers can book with {{ coachName }}.</p>
    </div>

    <!-- Progress dots + an always-visible Back, mirroring event_wizard -->
    <div class="flex align-items-center justify-space-between m-b-m">
      <span class="flex gap-x-xxs">
        <span v-for="dot in 4" :key="dot" class="rounded-full"
              :style="{ height: '6px', width: dot === stepNumber ? '24px' : '12px' }" :class="dot <= stepNumber ? 'bg-green-500' : 'bg-gray-200'"></span>
      </span>
      <button v-if="stepNumber > 1" type="button" class="btn btn-link p-x-n p-y-n text-14 text-gray-500" data-cy="availability-header-back"
              @click="goBack(previousStep)">&larr; Back</button>
    </div>

    <!-- ====================== STEP 1: the schedule ====================== -->
    <div v-if="step === '1'">
      <h2 class="text-16 font-semi m-t-none m-b-xxs">When</h2>
      <p class="text-14 text-gray-500 m-b-m">Pick a time window and we'll slice it into bookable slots.</p>
      <div class="form-group">
        <label for="availability-date">Date</label>
        <input id="availability-date" v-model="startDate" name="date" type="date" class="form-control" data-cy="availability-date">
      </div>

      <div class="form-group">
        <label>Time Window</label>
        <div class="flex gap-x-sm">
          <div class="flex-1">
            <label for="availability-start-time" class="text-12 m-y-none tracking-wide">From</label>
            <input id="availability-start-time" v-model="startTime" name="start_time" type="time" class="form-control" data-cy="availability-start-time">
          </div>
          <div class="flex-1">
            <label for="availability-end-time" class="text-12 m-y-none tracking-wide">To</label>
            <input id="availability-end-time" v-model="endTime" name="end_time" type="time" class="form-control" data-cy="availability-end-time">
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="availability-length">Lesson Length</label>
        <select id="availability-length" v-model.number="lessonLength" name="lesson_length" class="form-control" data-cy="availability-length">
          <option v-for="length in lessonLengthOptions" :key="length" :value="length">{{ length }} minutes</option>
        </select>
        <p v-if="slotsPerDay > 0" class="text-14 text-gray-500 m-t-xxs m-b-none" data-cy="availability-slot-preview">
          Creates {{ slotsPerDay }} open {{ slotsPerDay === 1 ? 'slot' : 'slots' }} of {{ lessonLength }} minutes between {{ formattedWindow }}.
        </p>
        <p v-else-if="windowValid === false" class="text-14 text-red-600 m-t-xxs m-b-none">
          The time window must fit at least one lesson.
        </p>
      </div>

      <div class="form-group">
        <label class="block">Repeat Weekly</label>
        <div class="btn-group segmented-control m-b-sm" role="group" aria-label="Repeat">
          <button type="button" class="btn btn-default btn-compact" :class="{ active: repeatEnabled }"
                  @click="setRepeatEnabled(true)" data-cy="availability-repeat-yes">Yes</button>
          <button type="button" class="btn btn-default btn-compact" :class="{ active: !repeatEnabled }"
                  @click="setRepeatEnabled(false)" data-cy="availability-repeat-no">No</button>
        </div>
        <div v-if="repeatEnabled">
          <div class="flex gap-x-xxs m-t-xxs">
            <button v-for="day in weekdayOptions" :key="day.key" type="button"
                    @click="toggleWeekday(day.key)"
                    class="rounded-full border border-solid text-12 font-semi cursor-pointer"
                    :class="repeatDays[day.key] ? 'border-green-700 text-green-50 bg-green-700' : 'border-gray-300 text-gray-600 bg-white'"
                    :data-cy="`availability-repeat-${day.key}`"
                    style="width: 36px; height: 36px;">
              {{ day.label }}
            </button>
          </div>
          <div v-if="anyRepeatDays" class="m-t-sm">
            <label for="availability-repeat-until">Until</label>
            <input id="availability-repeat-until" v-model="repeatUntil" name="repeat_until" type="date" class="form-control" :min="startDate" data-cy="availability-repeat-until">
            <p v-if="repeatUntilInvalid" class="text-14 text-red-600 m-t-xxs m-b-none" data-cy="availability-repeat-until-error">
              The repeat-until date must be after the start date.
            </p>
          </div>
          <p v-if="repeatSummary && !repeatUntilInvalid" class="text-14 text-gray-500 m-t-xs m-b-none" data-cy="availability-repeat-summary">{{ repeatSummary }}</p>
        </div>
      </div>

      <p v-if="error" class="text-14 text-red-600">{{ errorMessage }}</p>
      <div class="m-t-m">
        <button type="button" class="btn btn-primary w-full" @click="nextFromSchedule" data-cy="availability-next-1">Next: Who can book?</button>
        <a v-if="!forAppEmbed" class="btn btn-white w-full m-t-sm" :href="dashboardPath">Cancel</a>
      </div>
    </div>

    <!-- ====================== STEP 2: who can book ====================== -->
    <div v-if="step === '2'">
      <h2 class="text-16 font-semi m-t-none m-b-xxs">Who can book these slots?</h2>
      <p class="text-14 text-gray-500 m-b-m">Link rosters to limit booking to those wrestlers &mdash; parents of anyone else won't see the open times. Coaches can always book any wrestler manually.</p>

      <div class="form-group">
        <div class="flex flex-wrap gap-xxs m-b-xs" v-if="selectedRosters.length > 0">
          <button
            v-for="roster in selectedRosters"
            :key="roster.id"
            type="button"
            @click="toggleRoster(roster)"
            class="btn btn-success btn-outline rounded-lg font-small"
            style="padding: 2px 6px;">
            {{ roster.name }}
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <search
            class="search-form"
            response-key="rosters"
            placeholder="Type the name of a roster..."
            :mutate-history="false"
            :allow-add="false"
            only-dismiss-model="Roster"
            :small-input="true"
            :display-all-on-empty="false"
            readable-model-name="roster"
            base-url="/api/v1/rosters">
          <template v-slot:list="slotProps">
            <div
                class="border-top m-t-sm m-b-xs p-t-sm p-b-xs cursor-pointer flex justify-space-between align-items-center p-r-sm"
                @click="toggleRoster(slotProps.result)">
              <div>
                {{ slotProps.result.name }}
              </div>
              <div class="border rounded-sm border-blue-200 border-solid font-small"
                   style="padding: 2px 6px;">
                Link
              </div>
            </div>
          </template>
        </search>
      </div>

      <!-- Same consequence-summary tone as the event wizard's check-in step -->
      <p class="text-14 m-b-none" :class="bookingAccessSummary.cssClass" data-cy="availability-booking-access">
        {{ bookingAccessSummary.message }}
      </p>

      <p v-if="error" class="text-14 text-red-600">{{ errorMessage }}</p>
      <div class="m-t-m">
        <button type="button" class="btn btn-primary w-full" @click="advanceTo('3')" data-cy="availability-next-2">Next: Extras</button>
        <button type="button" class="btn btn-white w-full m-t-sm" @click="goBack('1')">&larr; Back</button>
      </div>
    </div>

    <!-- ====================== STEP 3: extras (v-show keeps trix alive) ====================== -->
    <div v-show="step === '3'">
      <h2 class="text-16 font-semi m-t-none m-b-xxs">Extras <span class="text-14 font-normal text-gray-400">&mdash; all optional</span></h2>
      <p class="text-14 text-gray-500 m-b-m">Skip these and the slots still work.</p>

      <div class="form-group">
        <label>Location</label>
        <location-select
            :initial-location-id="locationId"
            :initial-location="locationFreeText"
            @change="locationChanged"
        ></location-select>
      </div>

      <div class="form-group">
        <label>Notes</label>
        <input id="availability-notes" type="hidden" name="content">
        <trix-editor input="availability-notes" class="trix-content"></trix-editor>
      </div>

      <p v-if="error" class="text-14 text-red-600">{{ errorMessage }}</p>
      <div class="m-t-m">
        <button type="button" class="btn btn-primary w-full" @click="advanceTo('4')" data-cy="availability-next-3">Next: Review</button>
        <button type="button" class="btn btn-white w-full m-t-sm" @click="goBack('2')">&larr; Back</button>
      </div>
    </div>

    <!-- ====================== STEP 4: review ====================== -->
    <div v-if="step === '4'">
      <h2 class="text-16 font-semi m-t-none m-b-m">Review</h2>
      <div class="rounded-xxs border border-solid border-gray-200">
        <div class="p-sm flex justify-space-between align-items-start gap-x-sm">
          <div>
            <p class="text-12 text-gray-400 m-b-none">When</p>
            <p class="text-14 font-semi text-gray-900 m-b-none" data-cy="availability-review-when">{{ reviewWhen }}</p>
            <p class="text-14 text-gray-600 m-b-none" data-cy="availability-review-slots">{{ slotsPerDay }} {{ slotsPerDay === 1 ? 'slot' : 'slots' }} of {{ lessonLength }} minutes per day</p>
            <template v-if="anyRepeatDays">
              <p class="text-12 text-gray-400 m-b-none m-t-sm">Repeat</p>
              <div class="flex gap-x-xxs m-t-xxs" data-cy="availability-review-repeat">
                <span v-for="day in weekdayOptions" :key="day.key"
                      class="rounded-full inline-flex align-items-center justify-center text-12 font-semi"
                      :class="repeatDays[day.key] ? 'text-green-50 bg-green-700' : 'text-gray-400 bg-gray-100'"
                      style="width: 24px; height: 24px;">
                  {{ day.label }}
                </span>
              </div>
              <p class="text-12 text-gray-400 m-b-none m-t-sm">Until</p>
              <p class="text-14 font-semi text-gray-900 m-b-none" data-cy="availability-review-until">{{ reviewRepeatUntil }}</p>
            </template>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('1')">Edit</button>
        </div>
        <div class="p-sm flex justify-space-between align-items-center gap-x-sm border-top">
          <div>
            <p class="text-12 text-gray-400 m-b-none">Who can book</p>
            <p class="text-14 font-semi m-b-none" :class="bookingAccessSummary.cssClass" data-cy="availability-review-rosters">{{ bookingAccessSummary.message }}</p>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('2')">Edit</button>
        </div>
        <div class="p-sm flex justify-space-between align-items-center gap-x-sm border-top">
          <div>
            <p class="text-12 text-gray-400 m-b-none">Location</p>
            <p v-if="reviewLocation" class="text-14 font-semi text-gray-900 m-b-none" data-cy="availability-review-location">{{ reviewLocation }}</p>
            <p v-else class="text-14 text-gray-400 m-b-none" data-cy="availability-review-location">No location set</p>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('3')">Edit</button>
        </div>
        <div v-if="hasReviewNotes" class="p-sm border-top" data-cy="availability-review-notes">
          <div class="flex justify-space-between align-items-center gap-x-sm">
            <div>
              <p class="text-12 text-gray-400 m-b-none">Notes</p>
              <a class="text-14 font-semi cursor-pointer text-green-600" @click="showReviewNotes = !showReviewNotes">
                {{ showReviewNotes ? 'Hide notes' : 'Show notes' }}
              </a>
            </div>
            <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('3')">Edit</button>
          </div>
          <div v-show="showReviewNotes" class="trix-content text-14 text-gray-900 m-t-xs" v-html="reviewNotesHtml"></div>
        </div>
      </div>

      <div class="bg-green-50 border border-solid border-green-200 rounded-xxs p-sm m-t-sm">
        <p class="text-14 text-green-700 m-b-none" data-cy="availability-review-total">
          <i class="fa-regular fa-calendar-check m-r-xxs"></i>
          <span class="font-semi">{{ totalSlots }} open {{ totalSlots === 1 ? 'slot' : 'slots' }}</span> will be created{{ anyRepeatDays ? ` through ${reviewRepeatUntil}` : '' }}.
        </p>
      </div>

      <p v-if="error" class="text-14 text-red-600 m-t-sm">{{ errorMessage }}</p>
      <div class="m-t-m">
        <ladda-button @lbClicked="createSlots" el-class="btn-primary w-full" :loading="saving">
          Create {{ totalSlots }} {{ totalSlots === 1 ? 'Slot' : 'Slots' }}
        </ladda-button>
      </div>
      <button type="button" class="btn btn-white w-full m-t-sm" @click="goBack('3')">Back</button>
    </div>
  </div>
</template>

<script>
import Search from '../../shared/search.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import LocationSelect from './location_select.vue';
import { plainTextFromTrix } from '../trix_text';
import { formTimeZone, differsFromBrowser } from '../event_time_zone';
import { trixFileAttachmentMix } from './mixins/trix_file_attachment_mix';
import { appEmbedMix } from './mixins/app_embed_mix';

// Guided availability creation for a single coach, structured like
// event_wizard.vue: when -> who can book -> extras -> review, with the same
// step history, native back, and exit confirmation mechanics. Slices the chosen
// time window into N discrete private_lesson events, one POST per slot, exactly
// like the old calendar modal did, so the API contract is unchanged.
export default {
  name: 'availability-wizard',
  components: {
    Search,
    LaddaButton,
    LocationSelect,
  },
  mixins: [trixFileAttachmentMix, appEmbedMix],
  props: {
    coach: {
      type: Object,
      required: true,
    },
    // Where Cancel and the post-create redirect land on the web (the embed
    // pops its native screen instead). The erb passes it so this component
    // doesn't hardcode the host page's URL scheme.
    dashboardPath: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      step: '1',
      // Embed-only stand-in for browser history, mirroring event_wizard
      stepHistory: [],
      saving: false,
      error: false,
      errorMessage: '',
      dirty: false,

      startDate: moment().format('YYYY-MM-DD'),
      startTime: '16:00',
      endTime: '17:00',
      lessonLength: 60,
      lessonLengthOptions: [30, 45, 60, 90, 120],

      repeatEnabled: false,
      repeatDays: {
        sun: false, mon: false, tue: false, wed: false, thu: false, fri: false, sat: false,
      },
      repeatUntil: '',

      selectedRosters: [],

      locationId: null,
      locationFreeText: '',
      payloadLocationId: null,
      payloadLocation: '',
      payloadLocationName: '',
      // The picked location's own IANA zone (null = follows the team). The
      // window is typed as wall-clock strings, so picking the location after
      // the times just re-reads them on its clock, as the event wizard does.
      locationTimeZone: null,
      // Location id locationTimeZone was resolved for (null = no location).
      // When it doesn't match the picked location the zone is unknown.
      zoneResolvedForLocationId: null,

      reviewNotesHtml: '',
      showReviewNotes: false,

      weekdayOptions: [
        {
          key: 'sun', label: 'S', long: 'Sun', wday: 0,
        },
        {
          key: 'mon', label: 'M', long: 'Mon', wday: 1,
        },
        {
          key: 'tue', label: 'T', long: 'Tue', wday: 2,
        },
        {
          key: 'wed', label: 'W', long: 'Wed', wday: 3,
        },
        {
          key: 'thu', label: 'T', long: 'Thu', wday: 4,
        },
        {
          key: 'fri', label: 'F', long: 'Fri', wday: 5,
        },
        {
          key: 'sat', label: 'S', long: 'Sat', wday: 6,
        },
      ],
    };
  },
  computed: {
    coachName() {
      return `${this.coach.first_name} ${this.coach.last_name}`;
    },
    stepNumber() {
      return parseInt(this.step, 10);
    },
    // Where the header Back lands: one step earlier, matching the per-step
    // Back buttons at the bottom
    previousStep() {
      return String(Math.max(this.stepNumber - 1, 1));
    },
    // Slots are on the gym's clock: a Central coach opening 4–6pm at an
    // Eastern gym means 4–6pm Eastern. No location zone = the browser's.
    windowStart() {
      if (!this.startDate || !this.startTime) {
        return null;
      }
      return moment.tz(`${this.startDate} ${this.startTime}`, 'YYYY-MM-DD HH:mm', formTimeZone(this.locationTimeZone));
    },
    windowEnd() {
      if (!this.startDate || !this.endTime) {
        return null;
      }
      return moment.tz(`${this.startDate} ${this.endTime}`, 'YYYY-MM-DD HH:mm', formTimeZone(this.locationTimeZone));
    },
    zoneUnknown() {
      return this.zoneResolvedForLocationId !== this.payloadLocationId;
    },
    // null while the fields are incomplete, then true/false
    windowValid() {
      if (!this.windowStart || !this.windowEnd) {
        return null;
      }
      return this.slotsPerDay > 0;
    },
    slotsPerDay() {
      if (!this.windowStart || !this.windowEnd) {
        return 0;
      }
      const totalMinutes = this.windowEnd.diff(this.windowStart, 'minutes');
      if (totalMinutes <= 0) {
        return 0;
      }
      return Math.floor(totalMinutes / this.lessonLength);
    },
    formattedWindow() {
      return `${moment(this.startTime, 'HH:mm').format('h:mm a')} and ${moment(this.endTime, 'HH:mm').format('h:mm a')}`;
    },
    anyRepeatDays() {
      return _.some(_.values(this.repeatDays));
    },
    repeatUntilInvalid() {
      if (!this.anyRepeatDays || !this.repeatUntil || !this.startDate) {
        return false;
      }
      return !moment(this.repeatUntil, 'YYYY-MM-DD').isAfter(moment(this.startDate, 'YYYY-MM-DD'), 'day');
    },
    repeatSummary() {
      if (!this.anyRepeatDays) {
        return null;
      }
      const days = this.weekdayOptions.filter((d) => this.repeatDays[d.key]).map((d) => d.long).join(' & ');
      const parts = [`Repeats every ${days}`];
      if (this.startTime && this.endTime) {
        parts.push(`${moment(this.startTime, 'HH:mm').format('h:mm a')}–${moment(this.endTime, 'HH:mm').format('h:mm a')}`);
      }
      parts.push(this.repeatUntil ? `until ${moment(this.repeatUntil, 'YYYY-MM-DD').format('MMM D, YYYY')}` : 'until…');
      return parts.join(' · ');
    },
    // Consequence summary for the roster step, in the same tone as the event
    // wizard's check-in step: green when restricted, gray for the open default
    bookingAccessSummary() {
      if (this.selectedRosters.length > 0) {
        const names = this.selectedRosters.map((r) => r.name).join(', ');
        return { cssClass: 'text-green-700', message: `✓ Only wrestlers on ${names} can book these slots.` };
      }
      return { cssClass: 'text-gray-600', message: 'Any wrestler on the team can book these slots.' };
    },
    // How many days the schedule lands on: the anchor date plus every matching
    // weekday after it through repeat-until — mirrors Event#create_repeated_events!
    // (children start the day after the anchor, through inclusive).
    occurrenceCount() {
      if (!this.anyRepeatDays || !this.repeatUntil || this.repeatUntilInvalid) {
        return 1;
      }
      const selectedWdays = this.weekdayOptions.filter((d) => this.repeatDays[d.key]).map((d) => d.wday);
      let count = 1;
      const cursor = moment(this.startDate, 'YYYY-MM-DD').add(1, 'day');
      const through = moment(this.repeatUntil, 'YYYY-MM-DD');
      while (!cursor.isAfter(through, 'day')) {
        if (selectedWdays.includes(cursor.day())) {
          count += 1;
        }
        cursor.add(1, 'day');
      }
      return count;
    },
    totalSlots() {
      return this.slotsPerDay * this.occurrenceCount;
    },
    reviewWhen() {
      if (!this.windowStart || !this.windowEnd) {
        return '';
      }
      let when = `${this.windowStart.format('ddd MMM D')} · ${this.windowStart.format('h:mm a')}–${this.windowEnd.format('h:mm a')}`;
      if (differsFromBrowser(formTimeZone(this.locationTimeZone), this.windowStart)) {
        when += ` ${this.windowStart.format('z')}`;
      }
      return when;
    },
    reviewRepeatUntil() {
      if (!this.repeatUntil) {
        return '';
      }
      return moment(this.repeatUntil, 'YYYY-MM-DD').format('MMM D, YYYY');
    },
    reviewLocation() {
      if (this.payloadLocation && this.payloadLocation.trim()) {
        return this.payloadLocation.trim();
      }
      if (this.payloadLocationName && this.payloadLocationName.trim()) {
        return this.payloadLocationName.trim();
      }
      return null;
    },
    hasReviewNotes() {
      return plainTextFromTrix(this.reviewNotesHtml).length > 0;
    },
  },
  created() {
    this.applyQueryParamPrefill();
  },
  mounted() {
    // Browser back/forward walks the steps instead of leaving the page
    // (web only — in the embed the native nav stack owns back)
    if (!this.forAppEmbed) {
      history.replaceState({ wizardStep: this.step }, '');
      window.addEventListener('popstate', this.popStateHandler);
    }
    this.listenForNativeBack(this.nativeBackRequested);
  },
  destroyed() {
    if (!this.forAppEmbed) {
      window.removeEventListener('popstate', this.popStateHandler);
    }
    this.stopListeningForNativeBack();
  },
  watch: {
    step() {
      this.error = false;
      this.errorMessage = '';
    },
    startDate() { this.markDirty(); },
    startTime() { this.markDirty(); },
    endTime() { this.markDirty(); },
    lessonLength() { this.markDirty(); },
    repeatUntil() { this.markDirty(); },
  },
  methods: {
    // Guard the native back button/swipe once there's input worth losing; a
    // successful create pops via the bridge, which bypasses the guard by design
    markDirty() {
      if (this.dirty) {
        return;
      }
      this.dirty = true;
      this.setNativeExitConfirmation(true, 'Discard this availability?');
    },
    goTo(step) {
      // Snapshot notes off the live trix editor whenever we enter review —
      // covers the Next button and browser back/forward landing on review
      if (step === '4') {
        this.reviewNotesHtml = $('#availability-notes').val() || '';
      }
      this.step = step;
      window.scrollTo({ top: 0 });
    },
    advanceTo(step) {
      if (this.forAppEmbed) {
        this.stepHistory.push(this.step);
      }
      this.goTo(step);
      if (!this.forAppEmbed) {
        history.pushState({ wizardStep: step }, '');
      }
    },
    goBack(fallbackStep) {
      if (this.forAppEmbed) {
        this.goTo(this.stepHistory.pop() || fallbackStep);
      } else {
        history.back();
      }
    },
    popStateHandler(event) {
      if (event.state && 'wizardStep' in event.state) {
        this.goTo(event.state.wizardStep);
      }
    },
    nativeBackRequested() {
      if (this.stepNumber > 1) {
        this.goBack(this.previousStep);
      }
    },
    // The dashboard's "Add hours for Tuesday" shortcuts pass the target
    // date/times in the query string, same pattern as the calendar -> event
    // wizard handoff
    applyQueryParamPrefill() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('date')) {
        this.startDate = params.get('date');
      }
      if (params.get('start_time')) {
        this.startTime = params.get('start_time');
      }
      if (params.get('end_time')) {
        this.endTime = params.get('end_time');
      }
    },
    setRepeatEnabled(enabled) {
      this.markDirty();
      this.repeatEnabled = enabled;
      if (!enabled) {
        this.weekdayOptions.forEach((day) => {
          this.repeatDays[day.key] = false;
        });
        this.repeatUntil = '';
      }
    },
    toggleWeekday(key) {
      this.markDirty();
      this.repeatDays[key] = !this.repeatDays[key];
    },
    toggleRoster(roster) {
      const vm = this;
      vm.markDirty();
      // Clear search results after a pick
      vm.$notificationManager.emit('profile-clicked', 'Roster');
      const index = vm.selectedRosters.findIndex((r) => r.id === roster.id);
      if (index !== -1) {
        vm.selectedRosters.splice(index, 1);
      } else {
        vm.selectedRosters.push(roster);
      }
    },
    locationChanged({
      locationId, location, locationName, timeZone, ready,
    }) {
      this.markDirty();
      this.payloadLocationId = locationId;
      this.payloadLocation = location;
      this.payloadLocationName = locationName || '';
      // Step 3 re-mounts the picker, which emits before its list reloads; keep
      // the known zone until it can actually resolve the location's
      if (ready || !locationId) {
        this.locationTimeZone = timeZone || null;
        this.zoneResolvedForLocationId = locationId;
      }
      this.locationId = locationId;
      this.locationFreeText = location;
    },
    nextFromSchedule() {
      const vm = this;
      const missing = [];
      if (!vm.startDate) {
        missing.push('date');
      }
      if (!vm.startTime || !vm.endTime) {
        missing.push('time window');
      }
      if (vm.anyRepeatDays && !vm.repeatUntil) {
        missing.push('repeat until date');
      }
      if (missing.length > 0) {
        vm.error = true;
        vm.errorMessage = `Please fill in: ${missing.join(', ')}.`;
        return;
      }
      if (vm.slotsPerDay < 1) {
        vm.error = true;
        vm.errorMessage = 'The time window is too short for the lesson length — no slots would be created.';
        return;
      }
      if (vm.repeatUntilInvalid) {
        vm.error = true;
        vm.errorMessage = 'The repeat-until date must be after the start date.';
        return;
      }
      vm.advanceTo('2');
    },
    buildSlotPayloads() {
      const vm = this;
      const payloads = [];

      let repeat = null;
      if (vm.anyRepeatDays && vm.repeatUntil) {
        // Normalize to end-of-day so UTC offsets can't drop the final occurrence
        const untilString = moment.tz(vm.repeatUntil, 'YYYY-MM-DD', formTimeZone(vm.locationTimeZone))
          .endOf('day')
          .utc()
          .toISOString();
        repeat = {
          until: untilString,
          sun: vm.repeatDays.sun,
          mon: vm.repeatDays.mon,
          tue: vm.repeatDays.tue,
          wed: vm.repeatDays.wed,
          thu: vm.repeatDays.thu,
          fri: vm.repeatDays.fri,
          sat: vm.repeatDays.sat,
        };
      }

      for (let i = 0; i < vm.slotsPerDay; i += 1) {
        const slotStart = moment(vm.windowStart).add(i * vm.lessonLength, 'minutes');
        const slotEnd = moment(slotStart).add(vm.lessonLength, 'minutes');

        const payload = {
          event_type: 'private_lesson',
          start_at: slotStart.toISOString(),
          end_at: slotEnd.toISOString(),
          name: `Private Lesson - ${vm.coachName}`,
          color: '#27AA83',
          all_day: false,
          location: vm.payloadLocation,
          location_id: vm.payloadLocationId,
          notes: $('#availability-notes').val(),
          event_bookings_attributes: [
            {
              profile_id: vm.coach.id,
              profile_type: 'CoachProfile',
              status: 'pending',
              private_lesson_attributes: {
                status: 'available',
              },
            },
          ],
          roster_events_attributes: vm.selectedRosters.map((roster) => ({
            roster_id: roster.id,
          })),
        };
        if (repeat) {
          payload.repeat = repeat;
        }
        payloads.push(payload);
      }
      return payloads;
    },
    createSlots: _.throttle(function () {
      const vm = this;
      if (vm.saving) {
        return;
      }
      if (vm.zoneUnknown) {
        vm.error = true;
        vm.errorMessage = "We don't know the location's time zone yet. Wait a moment, or refresh the page.";
        return;
      }
      vm.saving = true;
      vm.error = false;

      const url = vm.$apiService.eventsUrl('');
      const posts = vm.buildSlotPayloads().map((event) => axios.post(url, { event }));

      Promise.allSettled(posts)
        .then((results) => {
          // A partial failure leaves the successful slots on the server — say
          // so instead of a bare error, or a retry would duplicate them
          const failures = results.filter((r) => r.status === 'rejected');
          if (failures.length > 0) {
            const created = results.length - failures.length;
            vm.saving = false;
            vm.error = true;
            vm.errorMessage = created > 0
              ? `${created} of ${results.length} slots were created before an error. ${vm.serverErrorMessage(failures[0].reason)} Check your availability before retrying.`
              : vm.serverErrorMessage(failures[0].reason);
            vm.notifyAppOfValidationError(vm.errorMessage);
            return;
          }
          const message = `${vm.totalSlots} open ${vm.totalSlots === 1 ? 'slot' : 'slots'} created`;
          vm.$notificationManager.emit('show-toast', message, true);
          if (vm.forAppEmbed) {
            vm.saving = false;
            // Tells the native app the slots were created, to pop to the root,
            // and to reload the availability screen
            vm.askAppToPop({
              name: 'availability_saved',
              data: {
                count: vm.totalSlots,
              },
            });
          } else {
            // Land back on the dashboard showing the day the slots were just
            // created for; keep the spinner going through the redirect
            window.location.assign(`${vm.dashboardPath}?date=${vm.startDate}`);
          }
        })
        .catch((error) => {
          vm.saving = false;
          vm.error = true;
          vm.errorMessage = vm.serverErrorMessage(error);
          vm.notifyAppOfValidationError(vm.errorMessage);
        });
    }, 500),
    serverErrorMessage(error) {
      const errors = _.get(error, 'response.data.errors', null);
      if (errors) {
        const messages = _.flatMap(_.values(errors));
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }
      return 'Error, please try again later or contact support.';
    },
  },
};
</script>
